import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Clock, Trophy, AlertCircle } from 'lucide-react';
import { PolicyQuiz, QuizQuestion, QuizAttempt } from '@/types/policy-reader';
import { useAuth } from '@/contexts/AuthContext';
import { formatDuration } from '@/utils/time-utils';
import { getQuizzesForPolicy, saveQuizAttempt, setCurentScreenForPolicy } from '@/app/pages/policy-reader/components/service';
import { useTenancy } from '@/contexts/TenancyContext';

interface PolicyQuizModalProps {
  policyId: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete?: (attempt: QuizAttempt) => void;
}

export const PolicyQuizModal: React.FC<PolicyQuizModalProps> = ({
  policyId,
  isOpen,
  onClose,
  onComplete
}) => {
  const { activeTenancy } = useTenancy();
  const { user } = useAuth();
  const [quiz, setQuiz] = useState<PolicyQuiz | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [showResults, setShowResults] = useState(false);
  const [quizAttempt, setQuizAttempt] = useState<QuizAttempt | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [startTime, setStartTime] = useState<Date>(new Date());

  const [toRetryQuiz, setToRetryQuiz] = useState<boolean>(false);


  /*
   * Sorts and compare the array of questions with the correct answers
   */
  function arraysEqualIgnoreOrder(a: string[], b: string[]): boolean {
    // console.log('[arraysEqualIgnoreOrder]', a, b)
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();

    const result = sortedA.every((val, idx) => val === sortedB[idx]);
    // console.log('[arraysEqualIgnoreOrder]', result);

    return result;
  }

  // Load quiz based on policy
  useEffect(() => {
    if (isOpen) {
      console.log('[PolicyQuizModal]', policyId);


      const loadQuizzes = async () => {
        // Load Quizzes
        const quiz = await getQuizzesForPolicy(activeTenancy, user, policyId);
        console.log('Load Quizzes', quiz);

        return quiz;
      }
      loadQuizzes().then(setQuiz);

      // // Determine which quiz to use based on policy ID or type
      // const quizKey = policyId.toLowerCase().includes('security') ? 'security-policy' : 'default';
      // const selectedQuiz = SYSTEM_QUIZZES[quizKey];

      // setQuiz(selectedQuiz);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setShowResults(false);
      setQuizAttempt(null);
      setStartTime(new Date());
      // setTimeRemaining((selectedQuiz.timeLimit || 10) * 60); // Convert to seconds
    }
  }, [isOpen, policyId]);

  // Timer countdown
  useEffect(() => {
    if (!isOpen || showResults || !quiz || quiz.timeLimit == 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleSubmitQuiz();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, showResults, quiz]);

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setAnswers({});
    setShowResults(false);
    setQuizAttempt(null);
    setStartTime(new Date());
  };

  const handleAnswerChange = (questionId: string, answer: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: [answer]
    }));
  };

  const handleNextQuestion = () => {
    if (quiz && currentQuestionIndex < quiz.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const calculateScore = (): { score: number; totalPoints: number; passed: boolean } => {
    if (!quiz) return { score: 0, totalPoints: 0, passed: false };

    let earnedPoints = 0;
    const totalPoints = quiz.questions.reduce((sum, q) => sum + q.points, 0);
    console.log('[totalPoints]', totalPoints, quiz.pass_mark);

    quiz.questions.forEach(question => {
      const userAnswer = answers[question.id];
      if (userAnswer === undefined || userAnswer === '') return;

      if (question.type === 'text') {
        // For text questions, give full points if answered (basic implementation)
        // In a real system, you might want more sophisticated text analysis
        if (userAnswer.trim().length > 10) {
          earnedPoints += question.points;
        }
      } else {
        // For multiple choice and true/false
        if (arraysEqualIgnoreOrder(userAnswer, question.correct_answers)) {
          earnedPoints += question.points;
        }
      }
    });

    const percentage = totalPoints > 0 ? (earnedPoints / totalPoints) * 100 : 0;
    const passed = percentage >= quiz.pass_mark;

    return { score: percentage, totalPoints: earnedPoints, passed };
  };

  const handleSubmitQuiz = async () => {
    if (!quiz || !user) return;

    const endTime = new Date();
    const timeSpent = endTime.getTime() - startTime.getTime();
    const { score, passed } = calculateScore();

    const attempt: QuizAttempt = {
      id: `attempt_${Date.now()}`,
      quizId: quiz.id,
      policyId,
      userId: user.uid,
      startTime,
      endTime,
      answers,
      score,
      passed,
      timeSpent
    };

    setQuizAttempt(attempt);
    setShowResults(true);
    onComplete?.(attempt);

    // console.log('[handleSubmitQuiz]', attempt, quiz);

    await saveQuizAttempt(activeTenancy, user, quiz, attempt)
  };

  useEffect(() => {
    if (quizAttempt && !quizAttempt.passed) {
      setToRetryQuiz(true);
    } else {
      setToRetryQuiz(false);
    }
  }, [quizAttempt]);

  const renderQuestion = (question: QuizQuestion) => {
    const userAnswer = answers[question.id];

    switch (question.type) {
      case 'multiple-choice':
        return (
          <RadioGroup
            value={userAnswer?.toString() || ''}
            onValueChange={(value) => handleAnswerChange(question.id, parseInt(value))}
          >
            {question.options?.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <RadioGroupItem value={index.toString()} id={`${question.id}_${index}`} />
                <Label htmlFor={`${question.id}_${index}`} className="flex-1 cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case 'true-false':
        return (
          <RadioGroup
            value={userAnswer?.toString() || ''}
            onValueChange={(value) => handleAnswerChange(question.id, value)}
          >
            {question.options?.map((option, index) => {
              return (
                <div key={index} className="flex items-center space-x-2">
                  <RadioGroupItem value={option.id} id={`${question.id}_${index}`} />
                  <Label htmlFor={`${question.id}_${index}`} className="flex-1 cursor-pointer">
                    {option.text}
                  </Label>
                </div>
              )
            })}
          </RadioGroup>
        );

      case 'text':
        return (
          <Textarea
            value={userAnswer || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder="Enter your answer here..."
            className="min-h-[100px]"
          />
        );

      default:
        return null;
    }
  };

  const renderResults = () => {
    if (!quiz || !quizAttempt) return null;

    const { score, passed } = quizAttempt;


    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            {passed ? (
              <Trophy className="h-16 w-16 text-yellow-500" />
            ) : (
              <AlertCircle className="h-16 w-16 text-orange-500" />
            )}
          </div>
          <h3 className="text-2xl font-bold mb-2">
            {passed ? 'Congratulations!' : 'Quiz Complete'}
          </h3>
          <p className="text-muted-foreground mb-4">
            {passed
              ? 'You have successfully completed the quiz.'
              : 'You may want to review the policy and try again.'}
          </p>
          <div className="flex items-center justify-center space-x-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">{Math.round(score)}%</div>
              <div className="text-sm text-muted-foreground">Score</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-primary">{formatDuration(quizAttempt.timeSpent)}</div>
              <div className="text-sm text-muted-foreground">Time</div>
            </div>
          </div>
          <Badge variant={passed ? 'default' : 'secondary'} className="mt-4">
            {passed ? 'PASSED' : 'NEEDS REVIEW'}
          </Badge>
        </div>

        <div className="space-y-4">
          <h4 className="font-semibold">Question Review</h4>
          {quiz.questions.map((question, index) => {
            const userAnswer = quizAttempt.answers[question.id];
            // console.log('[quiz.questions.map]', question, quizAttempt.answers);
            // const isCorrect = question.type === 'text'
            //   ? userAnswer?.trim().length > 10
            //   : userAnswer === question.correctAnswer;

            const isCorrect = arraysEqualIgnoreOrder(userAnswer, question.correct_answers)


            return (
              <Card key={question.id} className={`border-l-4 ${isCorrect ? 'border-l-green-500' : 'border-l-red-500'}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-base">Question {index + 1}</CardTitle>
                    {isCorrect ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-500" />
                    )}
                  </div>
                  <p className="text-sm">{question.question}</p>
                </CardHeader>
                <CardContent>
                  {question.type !== 'text' && (
                    <div className="space-y-2 text-sm">
                      <p><strong>Your answer:</strong>
                        {question.options.find(item => item.id == userAnswer[0]).text || 'Unknown'}
                      </p>
                      <p><strong>Correct answer:</strong>
                        {question.options.find(item => item.id == question.correct_answers[0]).text || 'Unknown'}
                      </p>
                    </div>
                  )}
                  {question.explanation && (
                    <div className="mt-2 p-2 bg-muted rounded text-sm">
                      <strong>Explanation:</strong> {question.explanation}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  if (!quiz) return null;

  const progress = ((currentQuestionIndex + 1) / quiz.questions.length) * 100;
  const currentQuestion = quiz.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === quiz.questions.length - 1;
  const canProceed = answers[currentQuestion?.id] !== undefined && answers[currentQuestion?.id] !== '';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card">
        <DialogHeader>
          <DialogTitle>{quiz.title}</DialogTitle>
          <DialogDescription>{quiz.description}</DialogDescription>
        </DialogHeader>

        {!showResults ? (
          <div className="space-y-6">
            {/* Progress and Timer */}
            <div className="flex items-center justify-between">
              <div className="flex-1 mr-4">
                <div className="flex justify-between text-sm mb-1">
                  <span>Progress</span>
                  <span>{currentQuestionIndex + 1} of {quiz.questions.length}</span>
                </div>
                <Progress value={progress} className="h-2" />
              </div>
              {quiz.timeLimit != 0 && (
                <div className="flex items-center space-x-2 text-sm">
                  <Clock className="h-4 w-4" />
                  <span className={timeRemaining < 60 ? 'text-red-500 font-bold' : ''}>
                  {Math.floor(timeRemaining / 60)}:{(timeRemaining % 60).toString().padStart(2, '0')}
                </span>
                </div>
              )}
            </div>

            {/* Current Question */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  Question {currentQuestionIndex + 1}
                  {/*<Badge variant="outline" className="ml-2">*/}
                  {/*  {currentQuestion.points} points*/}
                  {/*</Badge>*/}
                </CardTitle>
                <p>{currentQuestion.text}</p>
              </CardHeader>
              <CardContent>
                {renderQuestion(currentQuestion)}
              </CardContent>
            </Card>
          </div>
        ) : (
          renderResults()
        )}

        <DialogFooter>
          {!showResults ? (
            <div className="flex justify-between w-full">
              <Button
                variant="outline"
                onClick={handlePreviousQuestion}
                disabled={currentQuestionIndex === 0}
              >
                Previous
              </Button>

              <div className="space-x-2">
                {isLastQuestion ? (
                  <Button
                    onClick={handleSubmitQuiz}
                    disabled={!canProceed}
                  >
                    Submit Quiz
                  </Button>
                ) : (
                  <Button
                    onClick={handleNextQuestion}
                    disabled={!canProceed}
                  >
                    Next
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <>
              {toRetryQuiz && (
                <Button
                variant="outline"
                onClick={resetQuiz}
                >
                  Retry
                </Button>
              )}
              <Button onClick={() => {
                console.log(quizAttempt.passed)
                onClose(quizAttempt.passed)
              }}>
                Close
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
